import React, { useState, useEffect } from "react";
import { Container, Row, Col, Card, Form, InputGroup, Spinner, Pagination } from "react-bootstrap";
import { fetchYoutubeVideos, YoutubeVideoData } from "../services/YoutubeVideoService";

const VideosPage: React.FC = () => {
  const [videos, setVideos] = useState<YoutubeVideoData[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [totalVideos, setTotalVideos] = useState(0);

  useEffect(() => {
    loadVideos();
  }, [currentPage]);

  const loadVideos = async () => {
    setLoading(true);
    try {
      const data = await fetchYoutubeVideos(currentPage, 12, searchTerm);
      setVideos(data.videos);
      setTotalPages(data.totalPages);
      setTotalVideos(data.total);
    } catch (error) {
      console.error("Error loading videos:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleSearch = (e: React.FormEvent) => {
    e.preventDefault();
    setCurrentPage(1);
    loadVideos();
  };

  const extractVideoId = (url: string) => {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|&v=)([^#&?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  };

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric'
    });
  };

  return (
    <Container className="py-5">
      <div className="text-center mb-5">
        <h1 className="mb-2" style={{ color: "#810081" }}>Videos</h1>
        <p className="text-muted">Explore our collection of videos</p>
        <div 
          className="mx-auto mb-4" 
          style={{ 
            width: "60px", 
            height: "3px", 
            background: "linear-gradient(45deg, #810081, #FFD600)", 
            borderRadius: "2px" 
          }}
        ></div>
      </div>

      <Form onSubmit={handleSearch} className="mb-4">
        <InputGroup>
          <Form.Control
            type="text"
            placeholder="Search videos by title..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{ borderRadius: "30px 0 0 30px", padding: "12px 20px" }}
          />
          <button 
            type="submit" 
            className="btn"
            style={{ 
              backgroundColor: "#810081", 
              color: "white", 
              borderRadius: "0 30px 30px 0",
              padding: "0 25px"
            }}
          >
            <i className="bi bi-search"></i> Search
          </button>
        </InputGroup>
      </Form>

      {loading ? (
        <div className="text-center py-5">
          <Spinner animation="border" role="status" style={{ color: "#810081" }}>
            <span className="visually-hidden">Loading...</span>
          </Spinner>
          <p className="mt-3 text-muted">Loading videos...</p>
        </div>
      ) : (
        <>
          {videos.length === 0 ? (
            <div className="text-center py-5">
              <div 
                className="rounded-circle d-inline-flex align-items-center justify-content-center mb-3"
                style={{ 
                  width: '80px', 
                  height: '80px', 
                  background: 'rgba(129, 0, 129, 0.1)'
                }}
              >
                <i className="bi bi-camera-video" style={{ fontSize: '2rem', color: '#810081' }}></i>
              </div>
              <h4 style={{ color: '#810081' }}>No Videos Found</h4>
              <p className="text-muted">
                {searchTerm ? "Try a different search term" : "Check back soon for new videos"}
              </p>
            </div>
          ) : (
            <>
              <p className="text-muted mb-4">Showing {videos.length} of {totalVideos} videos</p>
              <Row className="g-4">
                {videos.map((video) => {
                  const videoId = extractVideoId(video.videoUrl);
                  return (
                    <Col key={video._id} lg={4} md={6} className="mb-4">
                      <Card 
                        className="h-100 border-0 shadow-sm" 
                        style={{ 
                          borderRadius: '15px',
                          transition: "all 0.3s ease",
                          overflow: "hidden"
                        }}
                        onMouseEnter={(e) => {
                          e.currentTarget.style.transform = "translateY(-5px)";
                          e.currentTarget.style.boxShadow = "0 10px 20px rgba(129, 0, 129, 0.15)";
                        }}
                        onMouseLeave={(e) => {
                          e.currentTarget.style.transform = "translateY(0)";
                          e.currentTarget.style.boxShadow = "0 4px 6px rgba(0,0,0,0.1)";
                        }}
                      >
                        <div style={{ position: "relative" }}>
                          <a 
                            href={`https://www.youtube.com/watch?v=${videoId}`} 
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="video-thumbnail"
                          >
                            <Card.Img 
                              variant="top" 
                              src={video.thumbnailUrl} 
                              alt={video.title}
                              style={{ height: "200px", objectFit: "cover" }}
                            />
                            <div 
                              className="play-button"
                              style={{
                                position: "absolute",
                                top: "50%",
                                left: "50%",
                                transform: "translate(-50%, -50%)",
                                width: "60px",
                                height: "60px",
                                backgroundColor: "rgba(129, 0, 129, 0.8)",
                                borderRadius: "50%",
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                color: "white",
                                fontSize: "24px",
                                opacity: 0.9,
                                transition: "all 0.3s ease"
                              }}
                            >
                              <i className="bi bi-play-fill"></i>
                            </div>
                          </a>
                          <div 
                            style={{ 
                              position: "absolute", 
                              top: "10px", 
                              right: "10px",
                              background: "rgba(255,255,255,0.9)",
                              padding: "5px 10px",
                              borderRadius: "20px",
                              fontSize: "0.8rem",
                              color: "#810081"
                            }}
                          >
                            <i className="bi bi-calendar2 me-1"></i>
                            {formatDate(video.createdAt)}
                          </div>
                        </div>
                        <Card.Body>
                          <Card.Title style={{ fontSize: "1.1rem", fontWeight: "600", color: "#810081" }}>
                            {video.title}
                          </Card.Title>
                          {video.description && (
                            <Card.Text className="text-muted" style={{ fontSize: "0.9rem" }}>
                              {video.description.length > 100 
                                ? `${video.description.substring(0, 100)}...` 
                                : video.description}
                            </Card.Text>
                          )}
                          <a 
                            href={`https://www.youtube.com/watch?v=${videoId}`}
                            target="_blank" 
                            rel="noopener noreferrer"
                            className="btn btn-sm mt-2"
                            style={{ 
                              backgroundColor: "#FFD600", 
                              color: "#810081", 
                              borderRadius: "20px",
                              padding: "5px 15px",
                              fontWeight: "500"
                            }}
                          >
                            Watch Video <i className="bi bi-box-arrow-up-right ms-1"></i>
                          </a>
                        </Card.Body>
                      </Card>
                    </Col>
                  );
                })}
              </Row>

              {totalPages > 1 && (
                <div className="d-flex justify-content-center mt-5">
                  <Pagination>
                    <Pagination.First 
                      onClick={() => setCurrentPage(1)} 
                      disabled={currentPage === 1}
                    />
                    <Pagination.Prev 
                      onClick={() => setCurrentPage(prev => Math.max(prev - 1, 1))} 
                      disabled={currentPage === 1}
                    />
                    
                    {[...Array(totalPages)].map((_, index) => {
                      const pageNumber = index + 1;
                      // Show current page, and 1 page before and after
                      if (
                        pageNumber === 1 || 
                        pageNumber === totalPages || 
                        (pageNumber >= currentPage - 1 && pageNumber <= currentPage + 1)
                      ) {
                        return (
                          <Pagination.Item 
                            key={pageNumber} 
                            active={pageNumber === currentPage}
                            onClick={() => setCurrentPage(pageNumber)}
                          >
                            {pageNumber}
                          </Pagination.Item>
                        );
                      } else if (
                        (pageNumber === currentPage - 2 && currentPage > 3) || 
                        (pageNumber === currentPage + 2 && currentPage < totalPages - 2)
                      ) {
                        return <Pagination.Ellipsis key={pageNumber} />;
                      }
                      return null;
                    })}
                    
                    <Pagination.Next 
                      onClick={() => setCurrentPage(prev => Math.min(prev + 1, totalPages))} 
                      disabled={currentPage === totalPages}
                    />
                    <Pagination.Last 
                      onClick={() => setCurrentPage(totalPages)} 
                      disabled={currentPage === totalPages}
                    />
                  </Pagination>
                </div>
              )}
            </>
          )}
        </>
      )}
    </Container>
  );
};

export default VideosPage;